import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, ApiError } from "./api";
import { type StudySubject, withSubjectVisuals } from "./studySubjects";

let cachedSubjects: StudySubject[] | null = null;
let inflight: Promise<StudySubject[]> | null = null;

async function loadStudySubjects(token: string): Promise<StudySubject[]> {
  if (cachedSubjects) return cachedSubjects;
  if (!inflight) {
    inflight = api.student.subjects
      .list(token)
      .then((res) => {
        const items = (res.items || []).map(withSubjectVisuals);
        cachedSubjects = items;
        return items;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function useStudySubjects() {
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? null;
  const [subjects, setSubjects] = useState<StudySubject[]>(cachedSubjects ?? []);
  const [loading, setLoading] = useState(!cachedSubjects);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(!cachedSubjects);
    loadStudySubjects(token)
      .then((items) => {
        if (!cancelled) setSubjects(items);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : "Could not load courses");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { subjects, loading, error };
}

export function invalidateStudySubjectsCache() {
  cachedSubjects = null;
}
